// How a run links to the runs around it (linked runs phase 6, spec
// docs/specs/linked-runs-bulk-sort.md §7; Thomas T9: the per-run map first).
//
// No link table: a run's tips name what fed it (a delivery, or a bin whose
// source run is the run that made it), and the tips of the bins it made name
// the runs it fed. Pure grouping; the page reads the rows.

export interface InTip {
  bin_quantity: number
  net_weight_kg: number | string | null
  delivery_id: string | null
  bins: { source_run_id: string | null; runs: { run_number: string | null; run_kind: string | null } | null } | null
}

export interface OutTip {
  run_id: string
  bin_quantity: number
  net_weight_kg: number | string | null
  runs: { run_number: string | null } | null
}

export interface MadeBin { crate_count: number | null; crates_remaining: number | null; net_weight_kg: number | string | null; status: string }

export interface RunNode { runId: string; runNumber: string; runKind: string | null; count: number; kg: number }
export interface FedBy   { deliveries: { count: number; kg: number }; runs: RunNode[] }
export interface FedInto { runs: RunNode[]; inStock: { pallets: number; crates: number; kg: number } }

/** A kg or count that may arrive as a numeric string, or not at all. */
export const num = (v: number | string | null | undefined) => Number(v ?? 0)
const n = num

export function addRun(map: Map<string, RunNode>, runId: string, runNumber: string | null, runKind: string | null, count: number, kg: number) {
  const cur = map.get(runId) ?? { runId, runNumber: runNumber ?? runId.slice(0, 8), runKind, count: 0, kg: 0 }
  cur.count += count; cur.kg += kg
  map.set(runId, cur)
}

/** What fed a run: its delivery tips, and the bulk sorts whose pallets it
 *  tipped. An ordinary rebin bin's tip links nothing new (#4115 review), so a
 *  citrus run draws no card. */
export function fedBy(tips: InTip[]): FedBy {
  const deliveries = { count: 0, kg: 0 }
  const runs = new Map<string, RunNode>()
  for (const t of tips) {
    const src = t.bins?.source_run_id
    if (src && t.bins?.runs?.run_kind === 'BULK_SORT') addRun(runs, src, t.bins?.runs?.run_number ?? null, 'BULK_SORT', n(t.bin_quantity), n(t.net_weight_kg))
    else if (!src) { deliveries.count += n(t.bin_quantity); deliveries.kg += n(t.net_weight_kg) }
  }
  return { deliveries, runs: [...runs.values()].sort((a, b) => b.kg - a.kg) }
}

/** What a run fed: the runs its bins were tipped onto, and what is still in stock. */
export function fedInto(outTips: OutTip[], made: MadeBin[]): FedInto {
  const runs = new Map<string, RunNode>()
  for (const t of outTips) addRun(runs, t.run_id, t.runs?.run_number ?? null, null, n(t.bin_quantity), n(t.net_weight_kg))
  const inStock = { pallets: 0, crates: 0, kg: 0 }
  for (const b of made) {
    // Only a pallet that is really in stock (#4115 review: never CANCELLED).
    if (b.status !== 'IN_COLDROOM' && b.status !== 'REBINNED') continue
    inStock.pallets += 1
    inStock.crates += n(b.crates_remaining ?? b.crate_count)
    inStock.kg += n(b.net_weight_kg)
  }
  return { runs: [...runs.values()].sort((a, b) => b.kg - a.kg), inStock }
}

/** Worth drawing only when a run links to another run (a citrus run that
 *  only takes deliveries and makes pallets has nothing new to show). */
export function linksAnotherRun(by: FedBy, into: FedInto): boolean {
  return by.runs.length > 0 || into.runs.length > 0 || into.inStock.pallets > 0
}
